/**
 * ⚠️ THE BUG THIS EXISTS FOR: Stage 4 locked up with "Maximum update depth exceeded".
 *
 * OffersStep re-maps offers onto sites once the sites are loaded. That effect read `localOffers`,
 * wrote `localOffers`, and listed `localOffers` in its own dependency array:
 *
 *     if (needsRemapping) { setLocalOffers(mapOffersWithSites(localOffers)); ... }
 *     }, [sites.length, localOffers, setOffers, mapOffersWithSites]);
 *
 * That is only safe while every pass makes progress. It does not when an offer's site cannot be
 * matched at all: mapOffersWithSites' third strategy hands such an offer back UNCHANGED, so
 * `needsRemapping` is still true next time — but `setLocalOffers` was called with a brand new
 * array, which re-ran the effect, which built another new array, forever. React gave up after
 * its nested-update limit and the error boundary took the page down.
 *
 * One unmatchable offer is enough, and arriving from Stage 1 is how you get one: that step
 * rebuilds `sites` from the form, so an offer still carrying a database site id — or a site that
 * has just been removed — matches nothing.
 *
 * The missing condition is simply "did anything actually move". These are the two pure halves of
 * that question, so the effect can decide without depending on the state it writes.
 */

type HasSiteId = { siteId?: string | null };

/**
 * True when at least one offer points at a site the tender no longer has — the condition the
 * re-map exists to repair.
 */
export const offersNeedRemapping = (
  offers: readonly HasSiteId[],
  siteIds: readonly (string | null | undefined)[],
): boolean => {
  if (!offers?.length || !siteIds?.length) return false;
  const known = new Set(siteIds.map(id => String(id ?? '')));
  return offers.some(offer => !known.has(String(offer?.siteId ?? '')));
};

/**
 * True when the re-map produced a different offer object for at least one position.
 *
 * ⚠️ This is the guard that ends the loop. A re-map that matched nothing returns every offer by
 * identity; storing that array would change only the ARRAY's identity, which is exactly what the
 * effect watches, and nothing else — so it must not be stored.
 */
export const remapChangedAnything = (
  before: readonly unknown[],
  after: readonly unknown[],
): boolean => {
  if (before.length !== after.length) return true;
  return after.some((offer, i) => offer !== before[i]);
};
